import { infiniteQueryOptions, queryOptions, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { api, type PaginationResponse } from '@/api';
import type { Insight, InsightRef } from '@/features/insights/types';
import { insightKey } from '@/features/insights/utils';

export const INSIGHTS_QUERY_KEY = 'insights' as const;

type InsightsScope =
  | { projectId: string; workspaceId?: never }
  | { projectId?: never; workspaceId: string };

export const insightsQuery = ({
  projectId,
  workspaceId,
  savedBy,
  search,
  pageSize = 20,
}: InsightsScope & { savedBy?: string; search?: string; pageSize?: number }) =>
  infiniteQueryOptions({
    queryKey: [
      INSIGHTS_QUERY_KEY,
      workspaceId ? 'workspace' : 'project',
      workspaceId ?? projectId,
      savedBy,
      search,
      pageSize,
    ],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<PaginationResponse<Insight>>(
          workspaceId
            ? `v1/evaluations/${workspaceId}/saved-insights`
            : `v1/projects/${projectId}/saved-insights`,
          { params: { savedBy, search, page: pageParam, pageSize } },
        )
      ).data,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length < lastPage.pageCount ? allPages.length + 1 : undefined,
  });

export const insightsCountQuery = (scope: InsightsScope) =>
  infiniteQueryOptions({
    ...insightsQuery({ ...scope, pageSize: 1 }),
    select: (data) => data.pages[0]?.totalCount ?? 0,
  });

export const insightRefsQuery = (projectId: string) =>
  queryOptions({
    queryKey: [INSIGHTS_QUERY_KEY, 'refs', projectId],
    queryFn: async () =>
      (await api.get<InsightRef[]>(`v1/projects/${projectId}/saved-insights/refs`)).data,
  });

export function useInsightKeys() {
  const { projectId } = useParams({ from: '/_authed/project/$projectId' });
  const { data } = useQuery(insightRefsQuery(projectId));
  return new Set(data?.map(insightKey));
}
